import { useState } from "react";
import AttendanceListPageView from "./AttendanceListPageView";
import StringHelper from "../../helpers/StringHelper";
import useEvaApi, { EvaApiCall } from "../../hooks/useEvaApi";
import useCustomEffect from "../../hooks/useCustomEffect";
import { useFrontofficeContext } from "../../context/FrontofficeContext";
import { useLoadContext } from "../../context/LoadContext";

function AttendanceListPage() {
  const loadContext = useLoadContext();
  const callEvaApi = useEvaApi();
  const [search, setSearch] = useState("");
  const frontofficeContext = useFrontofficeContext();
  const [attendanceListData, setAttendanceListData] = useState({
    visitors: [],
    employees: [],
  });

  /**
   * UseEffect for initializing the page
   */
  useCustomEffect(() => {
    fetchAttendanceListData();
  }, []);

  /**
   * Fetches the attendance list data from the API
   */
  async function fetchAttendanceListData() {
    await callEvaApi(
      new EvaApiCall("attendance_list")
        .setLoadingGroup("attendance_list_page_fetch")
        .setParams({
          with_visitor_visitor_type_data: true,
          with_visitor_employee_data: true,
          with_visitor_fields_data: true,
        })
        .setOnSuccess((response) => {
          setAttendanceListData({
            visitors: response.data.data.visitors,
            employees: response.data.data.employees,
          });
        })
    );
  }

  /**
   * This method will convert the attendance list data to the required format
   * @param {object} data
   * @returns {object}
   */
  function convertAttendanceListData(data) {
    const recordCount = data.visitors.length + data.employees.length;
    const presentCount =
      data.visitors.filter((vis) => vis.attendance_present).length +
      data.employees.filter((emp) => emp.attendance_present).length;

    return {
      visitors: data.visitors.filter((visitor) =>
        StringHelper.matchesSearch(
          frontofficeContext.getVisitorIdentifierString(visitor),
          search
        )
      ),
      employees: data.employees.filter((employee) =>
        StringHelper.matchesSearch(employee.name, search)
      ),
      record_count: recordCount,
      present_count: presentCount,
    };
  }

  /**
   * This method will handle the search input change
   * @param {event} event
   */
  function onSearchChange(event) {
    setSearch(event.target.value);
  }

  /**
   * This method will handle the present change
   * @param {int} id
   * @param {string} type
   * @param {bool} value
   */
  async function onPresentChange(id, type, value) {
    if (loadContext.isLoading(`attendance_list_page_fetch`)) return;

    await callEvaApi(
      new EvaApiCall(`attendance_list/${type}/${id}`)
        .setLoadingGroup(`attendance_list_page_${type}_${id}`)
        .setMethod("PUT")
        .setData({
          attendance_present: value,
        })
        .setAlertSuccess(false)
        .setOnSuccess(() => {
          fetchAttendanceListData();
        })
    );
  }

  return (
    <AttendanceListPageView
      attendanceListData={convertAttendanceListData(attendanceListData)}
      search={search}
      onSearchChange={onSearchChange}
      onPresentChange={onPresentChange}
      onRefresh={fetchAttendanceListData}
    />
  );
}

export default AttendanceListPage;
